// Pure save-flow decisions. Framework-free: no Vue, no axios, no I/O. These encode
// the choreography the PrescriptionSaveOrchestrator drives, so the gate order and
// the generic-prompt outcome live in exactly one place and are unit-tested against
// plain inputs.

import { Drug } from '@/models/Drug/Drug'
import { DrugSource } from '@/models/Drug/DrugProduct'
import { formatDateOnly } from './dateRules'

// ── billed-fill supersede identity ──────────────────────────────────────────

/**
 * The operator-editable fields of the server's duplicate-image guard, normalized like the
 * save payload (writtenDate via formatDateOnly, shared with PrescriptionService.save) —
 * keep the field set in sync with the guard's WHERE clause in usp_Prescription_Save.
 * StoreID is the one guard column deliberately omitted: it is editable, but changing it
 * makes the SP's current-fill lookup miss, so the save already falls into the INSERT
 * branch without the client having to detect it. PackageID is only an id within a
 * DrugSource namespace (GSDD/NonGSDD/Compound), so drug identity is the (drugSource,
 * packageId) pair, never the id alone.
 */
export interface FillIdentity {
    imageId: number | null
    patientId: number | null
    packageId: number | null
    drugSource: DrugSource | null
    writtenDate: string | null
}

/** A billed fill armed for Save-and-New supersede: the Rx id plus its identity at billing time. */
export type BilledFill = { rxId: string } & FillIdentity

export function captureFillIdentity(rx: {
    imageID?: number | null
    patientID?: number | null
    packageID?: number | null
    drugSource?: DrugSource | null
    writtenDate?: string | Date | null
}): FillIdentity {
    return {
        imageId: rx.imageID ?? null,
        patientId: rx.patientID ?? null,
        packageId: rx.packageID ?? null,
        drugSource: rx.drugSource ?? null,
        writtenDate: rx.writtenDate ? formatDateOnly(rx.writtenDate) : null,
    }
}

/**
 * Supersede (insert a new Rx) only when the form no longer identifies the billed fill —
 * any guard identity field (image, patient, drug, written date) changed. The changed
 * identity is exactly what lets the insert pass the server's duplicate-image guard, and
 * it preserves the billed, claim-bearing fill as it was adjudicated. With the identity
 * unchanged the operator is editing the billed Rx: save it as a normal update (rxNumber
 * intact, no guard on the server's UPDATE path).
 */
export function shouldSupersedeBilledFill(
    billedFill: BilledFill | null,
    rxId: string,
    formFill: FillIdentity,
): boolean {
    if (!billedFill || rxId !== billedFill.rxId) return false
    return formFill.imageId !== billedFill.imageId
        || formFill.patientId !== billedFill.patientId
        || formFill.packageId !== billedFill.packageId
        || formFill.drugSource !== billedFill.drugSource
        || formFill.writtenDate !== billedFill.writtenDate
}

// ── evaluateSaveGates ───────────────────────────────────────────────────────

/**
 * The gate that must be satisfied next, or 'proceed' when all are cleared.
 * The ORDER encoded in evaluateSaveGates is behavior and is pinned by tests.
 */
export type SaveGateDecision =
    | { kind: 'blocked-deceased' }
    | { kind: 'blocked-positiveid-config'; reason: 'config-loading' | 'target-loading' }
    | { kind: 'need-equivalents-check' }
    | { kind: 'need-positiveid' }
    | { kind: 'need-generic-preflight' }
    | { kind: 'proceed' }

export interface SaveGateContext {
    /** newMode || newRefillMode — deceased-patient block only applies to new fills. */
    isNewOrRefill: boolean
    patientDeceased: boolean
    positiveIdEnabled: boolean
    positiveIdConfigLoaded: boolean
    /** positiveIdTargetId !== null — a token bound to Patient/0 would 403. */
    positiveIdTargetKnown: boolean
    hasVerifiedToken: boolean
    /** The <PositiveIdGate> ref is mounted and can requestVerification(). */
    positiveIdGatePresent: boolean
    /** The Stage-A equivalents gate still needs to run for this attempt (entry-point + config gated). */
    equivalentsCheckPending: boolean
    /** shouldRunGenericPromptPreflight() — a brand GSDD fill that may have generic equivalents. */
    genericPreflightPending: boolean
}

/**
 * Decide the next save gate. Order (pinned by spec):
 *   deceased → equivalents-check → positive-ID(readiness then verify) → generic-preflight → proceed.
 * Deceased blocks before ANY gate; the equivalents check runs before positive-ID
 * resolution; positive-ID verification precedes the generic preflight.
 */
export function evaluateSaveGates(ctx: SaveGateContext): SaveGateDecision {
    if (ctx.isNewOrRefill && ctx.patientDeceased) {
        return { kind: 'blocked-deceased' }
    }

    if (ctx.equivalentsCheckPending) {
        return { kind: 'need-equivalents-check' }
    }

    if (ctx.positiveIdEnabled && !ctx.positiveIdConfigLoaded) {
        return { kind: 'blocked-positiveid-config', reason: 'config-loading' }
    }
    if (ctx.positiveIdEnabled && !ctx.positiveIdTargetKnown) {
        return { kind: 'blocked-positiveid-config', reason: 'target-loading' }
    }
    if (ctx.positiveIdEnabled && !ctx.hasVerifiedToken && ctx.positiveIdGatePresent) {
        return { kind: 'need-positiveid' }
    }

    if (ctx.genericPreflightPending) {
        return { kind: 'need-generic-preflight' }
    }

    return { kind: 'proceed' }
}

// ── selectGenericPromptOutcome ──────────────────────────────────────────────

/**
 * Outcome of the generic-substitution preflight for a brand fill.
 * - save-current-is-generic: the current package is itself a generic — nothing to substitute.
 * - save-no-candidates: no active generic equivalent exists.
 * - check-already-kept: an existing fill with candidates — caller must check the DAW-override
 *   log before prompting (kept → save; not kept → prompt with these candidates).
 * - prompt: show the brand→generic prompt with these candidates.
 */
export type GenericPromptOutcome =
    | { kind: 'save-current-is-generic' }
    | { kind: 'save-no-candidates' }
    | { kind: 'check-already-kept'; candidates: Drug.EquivalentDrug[] }
    | { kind: 'prompt'; candidates: Drug.EquivalentDrug[] }

const isBrand = (status: string | undefined): boolean =>
    !!status && status.toLowerCase() === 'brand'

/**
 * Decide what to do with the equivalents returned for a brand fill. Candidates are the
 * active non-brand equivalents other than the current package, sorted stocked-first
 * (onHand descending). Brand/generic detection uses the endpoint's brandGenericStatus,
 * which locally-cached drug data lacks.
 */
export function selectGenericPromptOutcome(
    equivalents: Drug.EquivalentDrug[],
    currentPackageId: number,
    isExistingFill: boolean,
): GenericPromptOutcome {
    // The endpoint echoes the current package's own status; a positively-generic current
    // fill is not a brand-to-generic case (generic siblings don't matter).
    const current = equivalents.find(eq => eq.packageID === currentPackageId)
    if (current?.brandGenericStatus && !isBrand(current.brandGenericStatus)) {
        return { kind: 'save-current-is-generic' }
    }

    const candidates = equivalents
        .filter(eq =>
            eq.packageID !== currentPackageId &&
            !!eq.brandGenericStatus &&
            !isBrand(eq.brandGenericStatus))
        // Surface stocked generics first; out-of-stock alternatives still appear below.
        .sort((a, b) => (b.onHand ?? 0) - (a.onHand ?? 0))

    if (candidates.length === 0) {
        return { kind: 'save-no-candidates' }
    }

    // On an already-saved fill the brand may have been kept here before (recorded DAW
    // override); the caller checks the log before prompting. New entries/refills always prompt.
    if (isExistingFill) {
        return { kind: 'check-already-kept', candidates }
    }

    return { kind: 'prompt', candidates }
}

// ── decideBackDestination ───────────────────────────────────────────────────

/**
 * Where a back-navigation lands. Priority (mirrors the page's backToPreviousPage):
 * an ingestion always returns to the ingestion list; otherwise a dispense-error entry
 * returns to the DispenseErrors list; otherwise the previous route (or browser back).
 */
export type BackDestination = 'ingestion' | 'dispenseErrors' | 'previous'

export interface BackDestinationInput {
    fromIngestion: boolean
    /** Entered via the dispense-error route param (or an armed dispense error). */
    hasDispenseErrorRoute: boolean
}

export function decideBackDestination(input: BackDestinationInput): BackDestination {
    if (input.fromIngestion) return 'ingestion'
    if (input.hasDispenseErrorRoute) return 'dispenseErrors'
    return 'previous'
}

// ── decidePostSaveContinuation ──────────────────────────────────────────────

/**
 * One post-save continuation step. The decision is pure data; PrescriptionDetailsPage
 * executes each action — router navigation, ingestion completion, queue pop, form reset,
 * and the batch-finished emit all stay on the page. This replaces the page's nested
 * new Promise(async ...) .then chain with an ordered, unit-testable plan.
 */
export type PostSaveAction =
    /** Ingestion, finishing: re-point the URL at the saved Rx, replacing the queue-item URL. */
    | { kind: 'replaceRouteToSavedRx' }
    /** Ingestion, finishing: push a fresh saved-Rx URL (no queue item in the current URL). */
    | { kind: 'pushRouteToSavedRx' }
    /**
     * Mark the saved ingestion queue item complete. The full contract rides on the action so the
     * tested decision — not the executor — owns it: which queue item, whether to auto-mode advance
     * afterward (only when not staying), and whether a failure aborts the rest of the continuation.
     */
    | { kind: 'completeIngestion'; queueItemId: string; advanceAutoMode: boolean; abortOnFailure: boolean }
    /** Auto-mode advance for a routed Rx with no ingestion queue item to complete. */
    | { kind: 'advanceAutoMode' }
    /** Pop the next legacy in-page queue item (navigates when one exists). */
    | { kind: 'popQueue' }
    /**
     * Navigate back, unless auto-mode or the queue already advanced to the next item. The
     * destination (ingestion / dispense-errors / previous) is resolved LIVE by the page at
     * execution time — after any route re-point may have flipped fromIngestion — so it is not
     * snapshotted here.
     */
    | { kind: 'backToPrevious'; skipBillingValidation: boolean; clearQuery: boolean }
    /** Save-and-New: clear the form for the next Rx on the same image. */
    | { kind: 'resetFormForNewRx' }
    /** Emit batchFinished with the saved prescription (batch autorefill hand-off). */
    | { kind: 'emitBatchFinished' }

export interface PostSaveContext {
    finish: boolean
    goNextRx: boolean
    stayOnPage: boolean
    hasBillPlanCallback: boolean
    willAutorefill: boolean
    isBatchMode: boolean
    fromIngestion: boolean
    /** A saved ingestion queue item exists AND the saved Rx carries full store/rx/rf ids. */
    canCompleteIngestion: boolean
    /** The queue item id to complete (present whenever canCompleteIngestion is true). */
    savedQueueItemId: string | null
    hasRouteRxId: boolean
    autoModeEnabled: boolean
    routeHasQueueItemIdQuery: boolean
    routeIsPrescriptionDetails: boolean
}

/**
 * The shared continuation tail (the legacy inner-promise .then): stay put (no action); or on a
 * finishing save pop the queue then navigate back (the executor skips the nav when auto-mode or
 * the queue already advanced); or clear the form for the next Save-and-New. A billed non-finishing
 * save runs its callback on the page and adds no tail action.
 */
function postSaveTail(ctx: PostSaveContext): PostSaveAction[] {
    if (ctx.stayOnPage) return []
    if (ctx.finish) {
        return [
            { kind: 'popQueue' },
            { kind: 'backToPrevious', skipBillingValidation: true, clearQuery: true },
        ]
    }
    if (!ctx.hasBillPlanCallback) return [{ kind: 'resetFormForNewRx' }]
    return []
}

/**
 * Decide the ordered post-save continuation. Branch order (pinned by spec):
 *   goNextRx → ingestion route re-point → complete-ingestion → routed-Rx (auto-mode | back)
 *   → non-finishing tail → unreachable finishing case.
 */
export function decidePostSaveContinuation(ctx: PostSaveContext): PostSaveAction[] {
    // Save-and-Next in a batch: emit the autorefill hand-off (when applicable); the page then
    // advances to the next Rx. The legacy nested promise was an inert rejected no-op here.
    if (ctx.goNextRx) {
        return ctx.willAutorefill ? [{ kind: 'emitBatchFinished' }] : []
    }

    const actions: PostSaveAction[] = []

    // On an ingestion (never in batch), a finishing save re-points the route at the saved Rx —
    // replacing when the URL still carries the queue item, otherwise pushing a fresh entry.
    if (!ctx.isBatchMode && ctx.fromIngestion && ctx.finish) {
        if (ctx.routeHasQueueItemIdQuery) actions.push({ kind: 'replaceRouteToSavedRx' })
        else if (ctx.routeIsPrescriptionDetails) actions.push({ kind: 'pushRouteToSavedRx' })
    }

    if (ctx.canCompleteIngestion) {
        actions.push(
            {
                kind: 'completeIngestion',
                queueItemId: ctx.savedQueueItemId as string,
                advanceAutoMode: !ctx.stayOnPage,
                abortOnFailure: ctx.finish,
            },
            ...postSaveTail(ctx),
        )
        return actions
    }

    if (ctx.hasRouteRxId && ctx.finish) {
        if (ctx.autoModeEnabled) {
            actions.push({ kind: 'advanceAutoMode' }, ...postSaveTail(ctx))
        } else {
            // Legacy: an immediate back-navigation with no queue pop and no billing-skip.
            actions.push({ kind: 'backToPrevious', skipBillingValidation: false, clearQuery: false })
        }
        return actions
    }

    if (!ctx.finish) {
        actions.push(...postSaveTail(ctx))
        return actions
    }

    // A finishing save with neither an ingestion queue item nor a routed Rx id is the legacy
    // "something went wrong" case: no actions here; the page logs the unexpected state.
    return actions
}
